import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ActionsSection from './ActionsSection';
import CompanySection from './CompanySection';
import EmergencySection from './EmergencySection';
import EventsSection from './EventsSection';
import NotificationsSection from './NotificationsSection';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import {
  actionShortcuts,
  eventSettings,
  notificationSettings,
} from '../../../utils/settingsDemoData';
import settingService from '../../../services/settingService';
import { selectUserData } from '../../../reducers/user.reducer';
import { getCurrentCompanyInfo } from '../../../utils/companySession';

const buildDefaultNotificationState = () => {
  const defaults = {};
  notificationSettings.items.forEach((item) => {
    defaults[item.id] = item.defaultEnabled ?? false;
  });
  return defaults;
};

const notificationResponseKeys = {
  //theme: ['theme', 'themeNotification'],
  stake: ['stake', 'stakeNotification'],
  odds: ['odds', 'oddNotification', 'oddsNotification'],
  result: ['result', 'resultNotification'],
  settlement: ['settlement', 'settlementNotification'],
};

const getNotificationKeys = (item) =>
  notificationResponseKeys[item.id] ?? [item.id, `${item.id}Notification`];

const toBooleanSetting = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalizedValue)) return true;
    if (['false', '0', 'no', 'off'].includes(normalizedValue)) return false;
  }
  return fallback;
};

const getDirectNotificationValue = (source, notificationKeys) => {
  if (!source || typeof source !== 'object') return undefined;
  const matchedKey = notificationKeys.find((key) => key in source);
  return matchedKey ? source[matchedKey] : undefined;
};

const getNotificationValue = (settings, item) => {
  const notifications = settings?.notifications ?? settings?.notification ?? settings;
  const notificationKeys = getNotificationKeys(item);

  if (Array.isArray(notifications)) {
    const matchedNotification = notifications.find((notification) => {
      if (getDirectNotificationValue(notification, notificationKeys) !== undefined) {
        return true;
      }

      const notificationKey =
        notification?.id ??
        notification?.key ??
        notification?.name ??
        notification?.type ??
        notification?.notificationType;

      return notificationKeys.some((key) => String(notificationKey) === String(key));
    });

    const directValue = getDirectNotificationValue(matchedNotification, notificationKeys);

    return (
      directValue ??
      matchedNotification?.enabled ??
      matchedNotification?.isEnabled ??
      matchedNotification?.value ??
      matchedNotification?.status ??
      item.defaultEnabled ??
      false
    );
  }

  if (notifications && typeof notifications === 'object') {
    const directValue = getDirectNotificationValue(notifications, notificationKeys);
    return directValue ?? item.defaultEnabled ?? false;
  }

  return item.defaultEnabled ?? false;
};

const normalizeNotificationState = (settings) => {
  const notificationStates = {};
  notificationSettings.items.forEach((item) => {
    notificationStates[item.id] = toBooleanSetting(
      getNotificationValue(settings, item),
      item.defaultEnabled ?? false,
    );
  });
  return notificationStates;
};

const getNotificationPayload = (notificationState) =>
  notificationSettings.items.reduce((payload, item) => {
    payload[item.id] = Boolean(notificationState[item.id]);
    return payload;
  }, {});

const getSettingsFromResponse = (response) => {
  const responseData = response?.data?.data ?? response?.data;
  const settings = Array.isArray(responseData) ? responseData[0] : responseData;
  return settings && typeof settings === 'object' ? settings : {};
};

const areSetsEqual = (firstSet, secondSet) => {
  if (firstSet.size !== secondSet.size) return false;
  return Array.from(firstSet).every((value) => secondSet.has(value));
};

const normalizeLeagueList = (items = []) => items.flat().filter(Boolean);

const getLeagueId = (league) =>
  league?.id ?? league?.leagueId ?? league?.value ?? league;

const getLeagueLabel = (league) =>
  league?.label ?? league?.leagueName ?? league?.name ?? String(getLeagueId(league) ?? '');

const normalizeSelectedLeagueIds = (quickSelectionEvents = []) =>
  new Set(
    quickSelectionEvents
      .map((league) => getLeagueId(league))
      .filter((leagueId) => leagueId !== undefined && leagueId !== null),
  );

const buildLeagueLookup = (...leagueLists) =>
  leagueLists.flat().reduce((lookup, league) => {
    const leagueId = getLeagueId(league);
    if (leagueId === undefined || leagueId === null || lookup.has(leagueId)) {
      return lookup;
    }

    lookup.set(leagueId, {
      id: leagueId,
      label: getLeagueLabel(league),
    });
    return lookup;
  }, new Map());

const processingModeOptions = ['auto', 'manual', 'oddsManual'];

const normalizeProcessingMode = (mode) =>
  processingModeOptions.includes(mode) ? mode : 'auto';

const SettingsView = () => {
  const userInfo = useSelector(selectUserData);
  const [leagues, setLeagues] = useState([]);
  const currentCompanyInfo = useMemo(() => getCurrentCompanyInfo(userInfo), [userInfo]);
  const companyId = currentCompanyInfo?.companyId ?? userInfo?.userDetails?.companyId;
  const [serverSettings, setServerSettings] = useState({});
  const [pendingChange, setPendingChange] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [autoSelectEnabled, setAutoSelectEnabled] = useState(
    eventSettings.autoSelectDefault ?? false,
  );
  const [savedAutoSelectEnabled, setSavedAutoSelectEnabled] = useState(
    eventSettings.autoSelectDefault ?? false,
  );

  const [selectedLeagues, setSelectedLeagues] = useState(
    () => new Set(eventSettings.defaultSelected ?? []),
  );
  const [savedLeagues, setSavedLeagues] = useState(
    () => new Set(eventSettings.defaultSelected ?? []),
  );
  const [processingMode, setProcessingMode] = useState('auto');
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [savedProcessingMode, setSavedProcessingMode] = useState('auto');
  const [savedEmergencyMode, setSavedEmergencyMode] = useState(false);

  const [selectedActions, setSelectedActions] = useState(
    () => new Set(actionShortcuts.defaultSelected ?? []),
  );
  const [savedActions, setSavedActions] = useState(
    () => new Set(actionShortcuts.defaultSelected ?? []),
  );

  const maxActionSelections = actionShortcuts.maxSelectable ?? 5;

  const [notificationState, setNotificationState] = useState(buildDefaultNotificationState);

  const selectedLeagueCount = useMemo(() => selectedLeagues.size, [selectedLeagues]);
  const hasActionChanges = useMemo(
    () => !areSetsEqual(selectedActions, savedActions),
    [selectedActions, savedActions],
  );
  const hasEventChanges = useMemo(
    () =>
      autoSelectEnabled !== savedAutoSelectEnabled ||
      !areSetsEqual(selectedLeagues, savedLeagues),
    [autoSelectEnabled, savedAutoSelectEnabled, selectedLeagues, savedLeagues],
  );
  const hasEmergencyChanges =
    processingMode !== savedProcessingMode || emergencyMode !== savedEmergencyMode;

  useEffect(() => {
    if (!companyId) return;

    settingService.get(companyId).then((response) => {
      const settings = getSettingsFromResponse(response);

      setServerSettings(settings);
      const nextLeagues = normalizeSelectedLeagueIds(settings.quickSelectionEvents ?? []);
      const nextAutoSelection = Boolean(settings.autoSelection);
      setSelectedLeagues(nextLeagues);
      setSavedLeagues(nextLeagues);
      setAutoSelectEnabled(nextAutoSelection);
      setSavedAutoSelectEnabled(nextAutoSelection);
      const nextEmergencyMode = Boolean(settings.emergencyMode);
      const nextProcessingMode = nextEmergencyMode
        ? normalizeProcessingMode(settings.processingMode)
        : 'auto';
      setProcessingMode(nextProcessingMode);
      setSavedProcessingMode(nextProcessingMode);
      setEmergencyMode(nextEmergencyMode);
      setSavedEmergencyMode(nextEmergencyMode);
      const nextActions = new Set(settings.quickActions ?? []);
      setSelectedActions(nextActions);
      setSavedActions(nextActions);
      setNotificationState(normalizeNotificationState(settings));
    }).catch((error) => {
      console.error('Error fetching settings:', error);
    });

    settingService.getLeagues().then((response) => {
      const formattedLeagues = response.data?.leagueData.map((v) => {
        return {
          id: v.leagueId,
          label: v.leagueName,
        }
      })
      setLeagues(formattedLeagues ?? []);
    }).catch((error) => {
      console.error('Error fetching leagues:', error);
    });

  }, [companyId]);

  const buildSettingsPayload = ({
    nextAutoSelectEnabled = autoSelectEnabled,
    nextSelectedLeagues = selectedLeagues,
    nextSelectedActions = selectedActions,
    nextNotificationState = notificationState,
    nextProcessingMode = processingMode,
    nextEmergencyMode = emergencyMode,
  } = {}) => {
    const leagueLookup = buildLeagueLookup(
      leagues,
      normalizeLeagueList(eventSettings.defaultLeagues),
      serverSettings.quickSelectionEvents ?? [],
    );

    return {
      ...serverSettings,
      autoSelection: Boolean(nextAutoSelectEnabled),
      quickSelectionEvents: Array.from(nextSelectedLeagues).map((leagueId) => (
        leagueLookup.get(leagueId) ?? {
          id: leagueId,
          name: String(leagueId),
        }
      )),
      quickActions: Array.from(nextSelectedActions),
      notifications: getNotificationPayload(nextNotificationState),
      emergencyMode: Boolean(nextEmergencyMode),
      processingMode: nextEmergencyMode ? normalizeProcessingMode(nextProcessingMode) : 'auto',
    };
  };

  const updateSettings = async (payload) => {
    if (!companyId) {
      toast.error('Unable to update settings because company is not selected.');
      return false;
    }

    try {
      const response = await settingService.update(companyId, payload);
      const updatedSettings = getSettingsFromResponse(response);
      setServerSettings(Object.keys(updatedSettings).length ? updatedSettings : payload);
      toast.success(response.data?.message ?? 'Settings updated successfully.');
      return true;
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(error?.response?.data?.message ?? 'Unable to update settings.');
      return false;
    }
  };

  const openConfirmation = (change) => {
    setPendingChange(change);
  };

  const handleLeagueToggle = (league) => {
    if (!autoSelectEnabled) return;

    const leagueId = getLeagueId(league);

    setSelectedLeagues((previousLeagues) => {
      const nextLeagues = new Set(previousLeagues);
      if (nextLeagues.has(leagueId)) {
        nextLeagues.delete(leagueId);
      } else {
        nextLeagues.add(leagueId);
      }
      return nextLeagues;
    });
  };

  const handleSaveEvents = () => {
    if (!hasEventChanges) return;

    openConfirmation({
      title: 'Save event settings?',
      description: 'Your event auto-selection and selected leagues will be updated for this workspace.',
      confirmLabel: 'Save Changes',
      apply: async () => {
        const payload = buildSettingsPayload({
          nextAutoSelectEnabled: autoSelectEnabled,
          nextSelectedLeagues: selectedLeagues,
        });
        const updated = await updateSettings(payload);
        if (updated) {
          setSavedAutoSelectEnabled(autoSelectEnabled);
          setSavedLeagues(new Set(selectedLeagues));
        }
      },
    });
  };

  const handleNotificationToggle = (notificationId, value) => {
    const notification = notificationSettings.items.find((item) => item.id === notificationId);

    openConfirmation({
      title: `${value ? 'Enable' : 'Disable'} ${notification?.label ?? 'notification'}?`,
      description: notification?.description ?? 'Update this notification setting.',
      confirmLabel: value ? 'Enable' : 'Disable',
      apply: async () => {
        const nextNotificationState = {
          ...notificationState,
          [notificationId]: value,
        };
        const payload = buildSettingsPayload({ nextNotificationState });
        const updated = await updateSettings(payload);
        if (updated) setNotificationState(nextNotificationState);
      },
    });
  };

  const handleActionToggle = (actionId) => {
    const isSelected = selectedActions.has(actionId);

    if (!isSelected && selectedActions.size >= maxActionSelections) {
      return;
    }

    setSelectedActions((previousActions) => {
      const nextActions = new Set(previousActions);
      if (nextActions.has(actionId)) {
        nextActions.delete(actionId);
      } else {
        nextActions.add(actionId);
      }
      return nextActions;
    });
  };

  const handleSaveActions = () => {
    if (!hasActionChanges) return;

    openConfirmation({
      title: 'Save quick actions?',
      description: 'Your selected quick actions will be updated for this workspace.',
      confirmLabel: 'Save Changes',
      apply: async () => {
        const payload = buildSettingsPayload({ nextSelectedActions: selectedActions });
        const updated = await updateSettings(payload);
        if (updated) setSavedActions(new Set(selectedActions));
      },
    });
  };

  const handleEmergencyModeToggle = (value) => {
    setEmergencyMode(value);
    if (!value) {
      setProcessingMode('auto');
    }
  };

  const handleProcessingModeChange = (value) => {
    if (!emergencyMode) return;
    setProcessingMode(normalizeProcessingMode(value));
  };

  const handleSaveEmergency = () => {
    if (!hasEmergencyChanges) return;

    openConfirmation({
      title: 'Save emergency settings?',
      description: 'Your emergency mode and processing mode settings will be updated for this workspace.',
      confirmLabel: 'Save Changes',
      apply: async () => {
        const payload = buildSettingsPayload({
          nextEmergencyMode: emergencyMode,
          nextProcessingMode: emergencyMode ? processingMode : 'auto',
        });
        const updated = await updateSettings(payload);
        if (updated) {
          setSavedEmergencyMode(emergencyMode);
          setSavedProcessingMode(emergencyMode ? processingMode : 'auto');
        }
      },
    });
  };

  const handleAutoSelectToggle = (value) => {
    setAutoSelectEnabled(value);
  };

  const handleConfirmChange = async () => {
    if (!pendingChange?.apply) return;

    setIsUpdating(true);
    await pendingChange.apply();
    setIsUpdating(false);
    setPendingChange(null);
  };

  return (
    <div className="min-h-screen px-6 py-5 dark:text-customMuted text-background">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
        <header className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] dark:text-customMuted text-background">
            Control Center
          </p>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold dark:text-customHeading text-background">Settings</h1>
              <p className="text-sm dark:text-customMuted text-primary">
                Fine-tune your workspace preferences and notification rules.
              </p>
            </div>
            <span className="rounded-full border border-customBorder bg-customSurface px-4 py-2 text-xs font-semibold text-customHeading">
              {selectedLeagueCount} leagues selected
            </span>
          </div>
        </header>

        <CompanySection />

        <EventsSection
          leagues={leagues}
          data={eventSettings}
          autoSelectEnabled={autoSelectEnabled}
          onAutoSelectToggle={handleAutoSelectToggle}
          selectedLeagues={selectedLeagues}
          onLeagueToggle={handleLeagueToggle}
          onSave={handleSaveEvents}
          hasChanges={hasEventChanges}
        />

        <EmergencySection
          emergencyMode={emergencyMode}
          onEmergencyModeToggle={handleEmergencyModeToggle}
          processingMode={processingMode}
          onProcessingModeChange={handleProcessingModeChange}
          onSave={handleSaveEmergency}
          hasChanges={hasEmergencyChanges}
        />

        <ActionsSection
          data={actionShortcuts}
          selectedActionIds={selectedActions}
          onToggle={handleActionToggle}
          onSave={handleSaveActions}
          maxSelectable={maxActionSelections}
          hasChanges={hasActionChanges}
        />

        <NotificationsSection
          data={notificationSettings}
          states={notificationState}
          onToggle={handleNotificationToggle}
        />
      </div>

      <ConfirmationDialog
        isOpen={!!pendingChange}
        setIsOpen={(isOpen) => {
          if (!isOpen) setPendingChange(null);
        }}
        title={pendingChange?.title}
        contextText={pendingChange?.description}
        handleSubmit={handleConfirmChange}
        confirmLabel={pendingChange?.confirmLabel ?? 'Confirm'}
        isProcessing={isUpdating}
        appearance="warning"
      />
    </div>
  );
};

export default SettingsView;
